import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

import { useQueryClient } from '@tanstack/react-query';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import { FormDocumentRow } from '../../../components/Form/FormDocumentRow';
import { FormActionButtons } from '../../../components/Form/FormActionButton';
import { SignatureModal } from '../../../components/Form/SignatureModel';
// import { AttachmentSection } from '../../../components/Form/AttachmentSection';
import { PageLayoutWrapper } from '../../../components/FormPageLayoutWrapper';
import { DatalistInput } from '../../cases/components/dataListInput';
import { useSettings } from '../../../context/SettingContext';
import { fetchPloById, savePloDraft, submitPloForm } from '../../../services/PloTwoService';
import type { PLOFormTwoData } from '../../../types/PLOs/plo_two.type';
import type { GeoLevel } from '../../../types/database';
import type { User } from '../../../types/user';


export const INITIAL_PLO_FORM_TWO_DATA: PLOFormTwoData = {
  id: '',
  office: '',
  township: '',
  courtName: '',
  caseNumber: '',
  plaintiffOrAccused: '',
  interviewerDetails: '',
  courtSubmissionDate: '',
  chargeSection: '',
  incidentDate: '',
  incidentLocation: '',
  investigatorInfo: '',
  interviewReason: '',
  intervieweeName: '',
  interviewDate: '',
  interviewLocation: '',
  supportingOrganization: '',
  observerName: '',
  courtSummonsDate: '',
  finalOrderDate: '',
  examiningJudge: '',
  finalOrderSummary: '',

  // Dual Signatures & Remarks
  interviewerSignImage: '',
  interviewerSignName: '',
  interviewerSignDate: '',
  interviewerRemarks: '',

  headOfficerSignImage: '',
  headOfficerSignName: '',
  headOfficerSignDate: '',
  headOfficerRemarks: '',

  // Metadata
  submitDate: '',
  attachments: [],
  status: 'DRAFT',
};

interface LegalFormProps {
  id?: string;
  initialData?: Partial<PLOFormTwoData>;
}

const getCurrentLocalDate = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

export function PLOFormTwo({ id: propId, initialData }: LegalFormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  const id = propId || params.id;
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const [formData, setFormData] = useState<PLOFormTwoData>({
    ...INITIAL_PLO_FORM_TWO_DATA,
    ...initialData,
  });
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean>(
    initialData?.status === 'APPROVED' || formData.status === 'APPROVED'
  );
   const [selectedGeoLevels, setSelectedGeoLevels] = useState<GeoLevel[]>([]);
  const [activeSigningRole, setActiveSigningRole] = useState<'headofficer' | 'USER' | null>(null);

  const { settings } = useSettings();
  const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];
  const TOWNSHIP_OPTIONS = settings?.townships.map((t) => t.name) || [];

  const currentUser = JSON.parse(localStorage.getItem('user') || 'null') as User | null
  useEffect(() => {
    if (formData.status === 'APPROVED') {
      setIsApproved(true);
    }
  }, [formData.status]);

  // Fetch form data by ID
  useEffect(() => {
    if (!id) return;

    let isSubscribed = true;
    setLoading(true);
    setError(null);

    fetchPloById(id)
      .then((data: PLOFormTwoData) => {
        if (isSubscribed) {
          setFormData((prev) => ({
            ...prev,
            ...data,
            id: data.id || prev.id || id || '',
          }));
          if (data.status === 'APPROVED') {
            setIsApproved(true);
          }
        }
      })
      .catch((err: any) => {
        if (isSubscribed) {
          setError(err?.message || 'အချက်အလက်များ ရယူ၍ မရပါ');
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [id]);

  // Form is editable ONLY if status is DRAFT/RETURNED AND it has NOT been approved yet
  const isEditable = (formData.status === 'DRAFT') && !isApproved;

  const isFormSigned = Boolean(
    formData.headOfficerSignImage?.trim() && formData.interviewerSignImage?.trim() && 
    (formData.headOfficerSignName?.trim() || formData.interviewerSignName?.trim())
  );

  // Navigation Handler
  const handleBackNavigation = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ploFormTwo'] });
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/plo-form-two');
    }
  };

  // Form Field Event Handlers
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearDate = (name: keyof PLOFormTwoData) => {
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, [name]: '' }));
  };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!isEditable || !e.target.files) return;
  //   const filesArray = Array.from(e.target.files);
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: [...(prev.attachments || []), ...filesArray],
  //   }));
  // };

  // const handleRemoveFile = (index: number) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: (prev.attachments || []).filter((_, i) => i !== index),
  //   }));
  // };


  const handleOpenSignatureModal = (role: 'headofficer' | 'USER') => {
    if (!isEditable) return;
    setActiveSigningRole(role);
    setIsSigning(true);
  };


  const handleSaveSignature = () => {
  if (sigCanvasRef.current) {
    // Replace getTrimmedCanvas() with getCanvas()
    const canvas = sigCanvasRef.current.getCanvas();
    const dataUrl = canvas.toDataURL('image/png');
    
    if (activeSigningRole === 'headofficer') {
      setFormData((prev) => ({ ...prev, headOfficerSignImage: dataUrl }));
    } else {
      setFormData((prev) => ({ ...prev, interviewerSignImage: dataUrl }));
    }
    
    setIsSigning(false);
    setActiveSigningRole(null);
  }
};

  const handleClearSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    if (activeSigningRole === 'headofficer') {
      setFormData((prev) => ({ ...prev, headOfficerSignImage: '' }));
    } else {
      setFormData((prev) => ({ ...prev, interviewerSignImage: '' }));
    }
  };

  const handleSaveDraft = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isEditable || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const payload: Partial<PLOFormTwoData> = { ...formData, status: 'DRAFT' };

      const response = await savePloDraft(formData.id || id || null, payload);

      if (response && response.id) {
        setFormData((prev) => ({ ...prev, id: response.id, status: 'DRAFT' }));
      }

      alert('မူကြမ်း သိမ်းဆည်းပြီးပါပြီ (Draft Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      alert(err?.message || 'မူကြမ်း သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Approve & Save (အတည်ပြုသိမ်းဆည်းမည်)
  const handleApproveAndSave = async (e: React.MouseEvent) => {
      e.preventDefault();
      if (isSubmitting) return;
  
      const nameOfOfficer = (formData.officerName || '').trim();
      if (!formData.officerSignatureImage?.trim() || !nameOfOfficer) {
        alert('လက်မှတ် နှင့် အမည် ဖြည့်သွင်းပေးပါရန်။');
        return;
      }
  
      const nameOfProsecutor = (formData.prosecutorName || '').trim();
      if (!formData.prosecutorSignatureImage?.trim() || !nameOfProsecutor) {
        alert('လက်မှတ် နှင့် အမည် ဖြည့်သွင်းပေးပါရန်။');
        return;
      }
  
      try {
        setIsSubmitting(true);
        let targetId: string | undefined = formData.id || id;
        const submissionDate =
          formData.submitDate?.trim() || getCurrentLocalDate();
  
        const payload: PLOFormTwoData = {
          ...formData,
          id: targetId || '',
          officerName: nameOfOfficer,
          prosecutorName: nameOfProsecutor,
          submitDate: submissionDate,
          status: 'APPROVED',
        };
  
        const response = await savePloDraft(targetId || null, payload);
        const newId = response?.id || targetId;
  
        setFormData((prev) => ({
          ...prev,
          id: newId,
          officerName: nameOfOfficer,
          prosecutorName: nameOfProsecutor,
          submitDate: submissionDate,
          status: 'APPROVED',
        }));
  
        setIsApproved(true); // Locks form editing
        alert('အတည်ပြု သိမ်းဆည်းပြီးပါပြီ (Approved & Saved Successfully)');
      } catch (err: any) {
        console.error('Failed to approve and save:', err);
        alert(err?.message || 'အတည်ပြု သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
      } finally {
        setIsSubmitting(false);
      }
    };

  // Handler for Submit To Superior (အထက်သို့ တင်ပြမည်)
  const handleSubmitToSuperior = async (e: React.MouseEvent,
    targetLevels?: GeoLevel[] | null
  ) => {
    e.preventDefault();
    if (isSubmitting) return;

     // Use passed levels if available; otherwise use multi-select state
    const levelsToSubmit =
      targetLevels && targetLevels.length > 0 ? targetLevels : selectedGeoLevels;

    // Validate that at least one checkbox level is selected
    if (!levelsToSubmit || levelsToSubmit.length === 0) {
      alert('အထက်သို့ တင်ပြရန် အဆင့် (Level) အနည်းဆုံးတစ်ခု ရွေးချယ်ပေးပါရန်။');
      return;
    }

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;
      const submissionDate =
        formData.submitDate?.trim() || getCurrentLocalDate();

      if (!targetId) {
        const draftResponse = await savePloDraft(null, { ...formData, status: 'DRAFT' });
        targetId = draftResponse?.id;
        if (!targetId) {
          throw new Error('အချက်အလက် သိမ်းဆည်းခြင်း အိုင်ဒီ (ID) မရရှိပါ');
        }

        setFormData((prev) => ({
          ...prev,
          ...draftResponse,
          id: targetId!,
        }));
      }

      const nameOfInteviewer = (formData.interviewerSignName || '').trim();
      const nameOfHeadOfficer = (formData.headOfficerSignName || '').trim();
      const hasSigned = Boolean(formData.headOfficerSignImage?.trim() && nameOfHeadOfficer && formData.interviewerSignImage?.trim() && nameOfInteviewer);
   //   const nextStatus = hasSigned ? 'APPROVED' : 'SUBMITTED';

      if(!hasSigned){
        throw new Error('လက်မှတ်ထိုးပြီးမှသာ တင်ပြနိုင်ပါသည်');
      }

      const payload: PLOFormTwoData = {
        ...formData,
        id: targetId,
        interviewerSignName: nameOfInteviewer,
        headOfficerSignName: nameOfHeadOfficer,
        status: 'SUBMITTED',
        submitDate: submissionDate,
        targetGeoLevels: levelsToSubmit, // Array of selected levels
      };

      const response = await submitPloForm(targetId, payload);

      setFormData((prev) => ({
        ...prev,
        id: targetId,
        submitDate: submissionDate,
        status: response?.status || 'SUBMITTED',
      }));

      setIsApproved(true); // Locks form editing after submission
      alert(
        response?.status ==='SUBMITTED'
          ?  'အထက်သို့ တင်ပြပြီးပါပြီ (Submitted Successfully)' : 'တင်ပြမှုမအောင်မြင်ပါ'
      );
      navigate('/plo-form-two');
    } catch (err: any) {
      console.error('Failed to submit form:', err);
      alert(err?.message || 'တင်ပြခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDocumentRow = (label: string, name: keyof PLOFormTwoData) => (
    <FormDocumentRow
      label={label}
      name={name as string}
      value={formData[name] as string}
      isEditable={isEditable}
      onChange={handleChange}
    />
  );

  const renderDatePicker = (name: keyof PLOFormTwoData) => (
      <FormDatePicker
        name={name as string}
        value={(formData?.[name] as string) || ''}
        isEditable={isEditable}
        onChange={handleChange}
        onClear={handleClearDate}
      />
    );

  const GEO_LEVEL_MAP: Record<string, string> = {
    NATIONAL: 'ရုံးချုပ်',
    STATE: 'ပြည်နယ်/တိုင်း',
    DISTRICT: 'ခရိုင်',
    TOWNSHIP: 'မြို့နယ်',
  };

  const renderStageProgression = () => {
  const status = formData.status || 'DRAFT';

  // Extract and format Geo Level text
  const rawGeo = formData.targetGeoLevels || formData.geoLevel;
  const formatGeo = (geo: any) => {
    if (typeof geo === 'string') return GEO_LEVEL_MAP[geo] || geo;
    if (Array.isArray(geo)) {
      return geo
        .map((g) => GEO_LEVEL_MAP[g] || g)
        .filter(Boolean)
        .join(' | ');
    }
    return '';
  };
  const geoText = formatGeo(rawGeo);

  const STATUS_CONFIG: Record<
    string,
    { label: string; bg: string; text: string; border: string; dot: string }
  > = {
    DRAFT: {
      label: 'မူကြမ်း',
      bg: 'bg-slate-100',
      text: 'text-slate-600',
      border: 'border-slate-200',
      dot: 'bg-slate-400',
    },
    APPROVED: {
      label: 'ကိုယ်တိုင်အတည်ပြုပြီး',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      dot: 'bg-emerald-600',
    },
    SUBMITTED: {
      label: 'တင်ပြပြီး',
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
    },
    UNDER_REVIEW: {
      label: 'ကြည့်ရှုထားသည်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
    ATTACHED: {
      label: 'ပြင်ဆင်ချက်/ညွှန်ကြားချက်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
  };

    const currentConfig = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;

      if (loading) {
        return (
          <div className="flex items-center justify-center p-12 text-slate-500">
            <span className="animate-spin mr-2">⏳</span> အချက်အလက်များ ရယူနေပါသည်...
          </div>
        );
      }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 px-3.5 bg-slate-50 border border-slate-200 rounded-lg shadow-xs">
        {/* Left: Section Title & Location Subtext */}
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            လုပ်ငန်းစဉ် အခြေအနေ
          </span>
          {geoText && (
            <span className="text-xs text-slate-600 font-medium mt-0.5 flex items-center gap-1">
              <span className="text-slate-400">📍</span>
              <span>{geoText}</span>
            </span>
          )}
        </div>

        {/* Right: Modern Pill Badge */}
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${currentConfig.bg} ${currentConfig.text} ${currentConfig.border} self-start sm:self-center`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${currentConfig.dot}`} />
          <span>{currentConfig.label}</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-500">
        <span className="animate-spin mr-2">⏳</span> အချက်အလက်များ ရယူနေပါသည်...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 rounded-xl border border-rose-200 my-6 max-w-xl mx-auto">
        <p className="font-semibold">{error}</p>
        <button
          type="button"
          onClick={handleBackNavigation}
          className="mt-4 px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          နောက်သို့ ပြန်သွားမည်
        </button>
      </div>
    );
  }

 return (
  <PageLayoutWrapper
      handleBackNavigation={handleBackNavigation}
      renderStageProgression={renderStageProgression}
      isEditable={isEditable}
      isPreview={isPreview}
      readOnlyMessage={
        formData.status === 'APPROVED'
          ? 'စာရွက်စာတမ်းကို ကိုယ်တိုင်အတည်ပြုပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
          : formData.status === 'SUBMITTED'
            ? 'စာရွက်စာတမ်းကို အထက်သို့ တင်ပြပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
            : undefined
      }
      setIsPreview={setIsPreview}
      >

    {/* A4 Sheet Container */}
    {/* Main Document Layout Sheets Container */}
      <div className="bg-white p-6 md:p-12 rounded-xl border border-slate-200 shadow-sm select-text">
        
        {/* Top Header Label Metadata Section */}
<div className="mt-4 space-y-2">
  {/* Top Right Label */}
  <div className="text-right">
    <span className="text-slate-500 font-normal text-sm">
      အကြိုတွေ့ဆုံမေးမြန်းခြင်း အမှုလိုက်စာတွဲ
    </span>
  </div>

  {/* Centered Main Title */}
  <h1 className="text-lg font-bold text-center">
    ပြည်ထောင်စုသမ္မတမြန်မာနိုင်ငံတော်
  </h1>
  
  {/* ရုံး (Using Reusable DatalistInput) */}
  <div className="flex justify-center items-center gap-2">
    <DatalistInput
      name="office"
      value={formData.office}
      onChange={handleChange}
      placeholder=""
      options={OFFICE_OPTIONS}
    />
    <span className="w-24 text-left">ဥပဒေရုံး</span>
  </div>

  {/* မြို့နယ် (UPDATED USING PERMISSION FIELDS) */}
  <div className="flex justify-center items-center gap-2">
    <DatalistInput
      name="township"
      value={formData.township}
      onChange={handleChange}
      placeholder=""
      options={TOWNSHIP_OPTIONS}
    />
    <span className="w-24 text-left">မြို့နယ်</span>
  </div>
</div>

                <form className="space-y-4">
                  
                  {/* Main Block Section 1-5 */}
                  {renderDocumentRow('၁။ တရားရုံးအမည်', 'courtName')}
                  {renderDocumentRow('၂။ အမှုအမှတ်', 'caseNumber')}
                  {renderDocumentRow('၃။ တရားလိုနှင့်တရားခံအမည်', 'plaintiffOrAccused')}
                  {renderDocumentRow('၄။ တွေ့ဆုံမေးမြန်းသူ (အမှုလိုက်ဥပဒေအရာရှိ)', 'interviewerDetails')}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
                    <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
                      ၅။ တရားစွဲဆိုသည့်ရက်စွဲ
                    </label>
                    <div className="flex-grow max-w-xs">{renderDatePicker('courtSubmissionDate')}</div>
                  </div>

                  {/* (က) အခြေခံအမှုသတင်းအချက်အလက် */}
                  <div className="pt-4">
                    <h3 className="text-sm font-bold text-slate-900 mb-2">(က) အခြေခံအမှုသတင်းအချက်အလက်</h3>
                    <div className="pl-4 space-y-1">
                      {renderDocumentRow('• စွဲဆိုသည့်ဥပဒေ ပုဒ်မ', 'chargeSection')}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
                        <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
                          • အမှုဖြစ်ပွားသည့်ရက်စွဲ
                        </label>
                        <div className="flex-grow max-w-xs">{renderDatePicker('incidentDate')}</div>
                      </div>
                      {renderDocumentRow('• အမှုဖြစ်ပွားသည့်နေရာ', 'incidentLocation')}
                      {renderDocumentRow('• အမှုစစ်ရဲအရာရှိ', 'investigatorInfo')}
                    </div>
                  </div>

                  {/* (ခ) အကြံတွေ့ဆုံမေးမြန်းခြင်း သတင်းအချက်အလက် */}
                  <div className="pt-4">
                    <h3 className="text-sm font-bold text-slate-900 mb-2">(ခ) အကြံတွေ့ဆုံမေးမြန်းခြင်း သတင်းအချက်အလက်</h3>
                    <div className="pl-4 space-y-1">
                      
                      <div className="flex flex-col gap-1 py-1.5">
                        <label className="text-slate-800 font-medium text-sm">
                          • အကြံတွေ့ဆုံမေးမြန်းမှုပြုလုပ်ရသည့်အကြောင်းပြချက် -
                        </label>
                        <p className="text-xs text-slate-400 italic font-normal -mt-1">(ဤအမှုတွင် အကြံတွေ့ဆုံမေးမြန်းမှုပြုလုပ်ခြင်း သို့မဟုတ် မပြုလုပ်ခြင်း အကြောင်းပြချက်ကို ဖော်ပြပါ)</p>
                        <div className="relative w-full mt-1">
                          {isPreview ? (
                            <p className="text-sm text-slate-900 font-medium whitespace-pre-wrap bg-slate-50/50 p-2.5 rounded min-h-[3rem]">
                              {formData.interviewReason || <span className="text-slate-300 italic font-normal">(ကွက်လပ်)</span>}
                            </p>
                          ) : (
                            <input type="text" name="interviewReason" value={formData.interviewReason} onChange={handleChange} placeholder="........................................................................................................................................................................" className="w-full bg-transparent border-none p-0 text-sm focus:outline-none focus:ring-0" />
                          )}
                        </div>
                      </div>

                      {renderDocumentRow('• တွေ့ဆုံမေးမြန်းခံရသူ', 'intervieweeName')}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
                        <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
                          • အကြံတွေ့ဆုံမေးမြန်းမှုပြုလုပ်သည့်ရက်စွဲ
                        </label>
                        <div className="flex-grow max-w-xs">{renderDatePicker('interviewDate')}</div>
                      </div>
                      {renderDocumentRow('• အကြံတွေ့ဆုံမေးမြန်းမှုပြုလုပ်သည့်နေရာ', 'interviewLocation')}
                      
                      <div className="flex flex-col gap-1 py-1.5">
                        <label className="text-slate-800 font-medium text-sm">• အကူအညီပေးသည့် အဖွဲ့အစည်း</label>
                        <p className="text-xs text-slate-400 italic font-normal -mt-1">(အကြံတွေ့ဆုံမေးမြန်းမှုပြုလုပ်ရန် စီစဉ်ထောက်ပံ့ပေးသည့်/ကူညီပေးသည့် အဖွဲ့အစည်းကို ဖော်ပြပါ)</p>
                        <div className="relative w-full mt-1">
                          {isPreview ? (
                            <span className="text-sm text-slate-900 font-medium block min-h-[1.5rem] bg-slate-50/50 rounded px-1">{formData.supportingOrganization || ' '}</span>
                          ) : (
                            <input type="text" name="supportingOrganization" value={formData.supportingOrganization} onChange={handleChange} placeholder="........................................................................................................................................................................" className="w-full bg-transparent border-none p-0 text-sm focus:outline-none focus:ring-0" />
                          )}
                        </div>
                      </div>

                      {renderDocumentRow('• တွေ့ဆုံမေးမြန်းမှုတွင် တက်ရောက်သည့် လေ့လာသူ', 'observerName')}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
                      <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
                        • တွေ့ဆုံမေးမြန်းခံရသူ တရားရုံး သက်သေခံသည့်ရက်စွဲ
                      </label>
                      <div className="flex-grow max-w-xs">{renderDatePicker('courtSummonsDate')}</div>
                    </div>
                    </div>
                  </div>

                  {/* Bottom Structural Section Fields 6 and 7 */}
                  <div className="pt-4 space-y-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
                    <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
                      ၆။ အပြီးသတ်အမိန့်ချမှတ်သည့်ရက်စွဲ
                    </label>
                    <div className="flex-grow max-w-xs">{renderDatePicker('finalOrderDate')}</div>
                  </div>
                    {renderDocumentRow('၇။ စစ်ဆေးစီရင်သည့်တရားသူကြီး', 'examiningJudge')}
                  </div>

                  {/* ၈။ အပြီးသတ်အမိန့် Textarea Field Row */}
                  <div className="flex flex-col gap-1 py-2">
                    <label className="text-slate-800 font-medium text-sm">၈။ အပြီးသတ်အမိန့်</label>
                    <div className="relative w-full">
                      {isPreview ? (
                        <p className="text-sm text-slate-900 font-medium leading-relaxed whitespace-pre-wrap bg-slate-50/50 p-3 rounded min-h-[5rem]">
                          {formData.finalOrderSummary || <span className="text-slate-300 italic font-normal">(ကွက်လပ်)</span>}
                        </p>
                      ) : (
                        <textarea
                          name="finalOrderSummary"
                          rows={3}
                          value={formData.finalOrderSummary}
                          onChange={handleChange}
                          placeholder=""
                          className="w-full bg-transparent border-none p-2 text-sm text-slate-900 placeholder-slate-300 focus:outline-none focus:ring-0 font-medium leading-relaxed resize-none"
                          style={{ backgroundImage: 'linear-gradient(transparent, transparent 27px, #cbd5e1 27px, #cbd5e1 28px)', backgroundSize: '100% 28px', lineHeight: '28px' }}
                        />
                      )}
                    </div>
                  </div>

                  {/* ၉။ အမှုလိုက်ဥပဒေအရာရှိ သဘောထားမှတ်ချက် (Signature Set 1) */}
                  <div className="pt-6 border-t border-slate-100">
                    <div className="flex flex-col gap-1 py-1">
                      <label className="text-slate-900 font-bold text-sm">၉။ အမှုလိုက်ဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက်</label>
                      <div className="relative w-full mt-1">
                        {isPreview ? (
                          <p className="text-sm text-slate-800 bg-slate-50/50 p-2 rounded min-h-[3rem] whitespace-pre-wrap">{formData.interviewerRemarks || ' '}</p>
                        ) : (
                          <input type="text" name="interviewerRemarks" value={formData.interviewerRemarks} onChange={handleChange} placeholder="........................................................................................................................................................................" className="w-full bg-transparent border-none p-0 text-sm focus:outline-none focus:ring-0" />
                        )}
                      </div>
                    </div>

                    {/* Signature Block 1 */}
                    <div className="flex flex-col items-end gap-1.5 text-sm text-slate-800 pr-4 mt-4">
                      <div className="w-72 space-y-2">
                        <div className="flex items-center">
                          <span className="w-24 text-right text-sm whitespace-nowrap mr-2">လက်မှတ်</span>
                          {isPreview ? (
                            <div className="flex-1 min-h-[32px] flex items-center justify-center border-b border-dashed border-slate-300">
                              {formData.interviewerSignImage ? <img src={formData.interviewerSignImage} alt="Signature" className="h-8 object-contain mix-blend-multiply" /> : <span className="text-slate-300 italic text-xs">(ကွက်လပ်)</span>}
                            </div>
                          ) : (
                            <div onClick={() => handleOpenSignatureModal('USER')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-slate-50 relative group">
                              {formData.interviewerSignImage ? (
                                <>
                                  <img src={formData.interviewerSignImage} alt="Signature" className="h-8 object-contain mix-blend-multiply" />
                                  <span className="absolute inset-0 bg-black/40 text-white text-[10px] opacity-0 group-hover:opacity-100 flex items-center justify-center transition rounded">လက်မှတ်အသစ်ပြင်ရန်</span>
                                </>
                              ) : (
                                <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                              )}
                              {formData.interviewerSignImage && (
                              <button
                                type="button"
                                onClick={handleClearSignature}
                                title="လက်မှတ်ရှင်းလင်းမည်"
                                className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                              >
                                ✕
                              </button>
                            )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center">
                          <span className="w-24 text-right text-sm whitespace-nowrap mr-2">အမည်</span>
                          {isPreview ? <span className="flex-1 text-center font-medium border-b border-dashed border-slate-300 min-h-[24px]">{formData.interviewerSignName || '-'}</span> : <input type="text" name="interviewerSignName" value={formData.interviewerSignName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center p-0 text-sm" />}
                        </div>
                        <div className="flex items-center">
                          <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">ရက်စွဲ</span>
                          {isPreview ? <span className="flex-1 text-center font-medium border-b border-dashed border-slate-300 min-h-[24px]">{formData.interviewerSignDate || '-'}</span> : <div className="flex-grow">{renderDatePicker('interviewerSignDate')}</div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ၁၀။ တာဝန်ခံဥပဒေအရာရှိ သဘောထားမှတ်ချက် (Signature Set 2) */}
                  <div className="pt-6 border-t border-slate-100">
                    <div className="flex flex-col gap-1 py-1">
                      <label className="text-slate-900 font-bold text-sm">၁၀။ တာဝန်ခံဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက်</label>
                      <div className="relative w-full mt-1">
                        {isPreview ? (
                          <p className="text-sm text-slate-800 bg-slate-50/50 p-2 rounded min-h-[3rem] whitespace-pre-wrap">{formData.headOfficerRemarks || ' '}</p>
                        ) : (
                          <input type="text" name="headOfficerRemarks" value={formData.headOfficerRemarks} onChange={handleChange} placeholder="........................................................................................................................................................................" className="w-full bg-transparent border-none p-0 text-sm focus:outline-none focus:ring-0" />
                        )}
                      </div>
                    </div>

                    {/* Signature Block 2 */}
                    <div className="flex flex-col items-end gap-1.5 text-sm text-slate-800 pr-4 mt-4">
                      <div className="w-72 space-y-2">
                        <div className="flex items-center">
                          <span className="w-24 text-right text-sm whitespace-nowrap mr-2">လက်မှတ်</span>
                          {isPreview ? (
                            <div className="flex-1 min-h-[32px] flex items-center justify-center border-b border-dashed border-slate-300">
                              {formData.headOfficerSignImage ? <img src={formData.headOfficerSignImage} alt="Signature" className="h-8 object-contain mix-blend-multiply" /> : <span className="text-slate-300 italic text-xs">(ကွက်လပ်)</span>}
                            </div>
                          ) : (
                            <div onClick={() => handleOpenSignatureModal('headofficer')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-slate-50 relative group">
                              {formData.headOfficerSignImage ? (
                                <>
                                  <img src={formData.headOfficerSignImage} alt="Signature" className="h-8 object-contain mix-blend-multiply" />
                                  <span className="absolute inset-0 bg-black/40 text-white text-[10px] opacity-0 group-hover:opacity-100 flex items-center justify-center transition rounded">လက်မှတ်အသစ်ပြင်ရန်</span>
                                </>
                              ) : (
                                <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                              )}
                              {formData.headOfficerSignImage && (
                              <button
                                type="button"
                                onClick={handleClearSignature}
                                title="လက်မှတ်ရှင်းလင်းမည်"
                                className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                              >
                                ✕
                              </button>
                            )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center">
                          <span className="w-24 text-right text-sm whitespace-nowrap mr-2">အမည်</span>
                          {isPreview ? <span className="flex-1 text-center font-medium border-b border-dashed border-slate-300 min-h-[24px]">{formData.headOfficerSignName || '-'}</span> : <input type="text" name="headOfficerSignName" value={formData.headOfficerSignName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center p-0 text-sm" />}
                        </div>
                        <div className="flex items-center">
                          <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">ရက်စွဲ</span>
                          {isPreview ? <span className="flex-1 text-center font-medium border-b border-dashed border-slate-300 min-h-[24px]">{formData.headOfficerSignDate || '-'}</span> : <div className="flex-grow">{renderDatePicker('headOfficerSignDate')}</div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer Informative Note Disclaimer Description Text Block */}
                  <div className="pt-8 text-xs text-slate-500 leading-relaxed border-t border-slate-100 space-y-1">
                    <p className="font-bold text-slate-700">မှတ်ချက်။</p>
                    <p>ဤအမှုလိုက်စာတွဲအား အမှုလိုက်ဥပဒေအရာရှိက ပြည့်စုံစွာ ဖြည့်စွက်ပြီး အမှုအား အပြီးသတ် အမိန့်ချမှတ်ပြီးနောက် ( ၃ ) ရက်အတွင်း တာဝန်ခံဥပဒေအရာရှိထံ တင်ပြရမည်။ တာဝန်ခံဥပဒေအရာရှိက ဤအမှုလိုက် စာတွဲအား လက်ခံရရှိပြီး ( ၇ ) ရက်အတွင်း သက်ဆိုင်ရာ ပြည်နယ် သို့မဟုတ် တိုင်းဒေသကြီး ဥပဒေအရာရှိထံ တင်ပြရမည်။</p>
                  </div>

                  {/* Form Action Buttons Component Integration */}
                  <FormActionButtons
                    isPreview={isPreview}
                    isEditable={isEditable}
                    isSubmitting={isSubmitting}
                    isFormSigned={isFormSigned}
                    isApproved={isApproved}
                    targetGeoLevels={formData.targetGeoLevels}
                    currentGeoLevel={currentUser?.scopeLevel}
                    selectedGeoLevels={selectedGeoLevels}
                    onGeoLevelChange={setSelectedGeoLevels}
                    onCancel={handleBackNavigation}
                    onSaveDraft={handleSaveDraft}
                    onApproveAndSave={handleApproveAndSave}
                    onSubmitToSuperior={handleSubmitToSuperior}
                  />
                </form>

        {/* Signature Canvas Dialog */}
        {isSigning && isEditable && (
          <SignatureModal
            isOpen={isSigning}
            sigCanvasRef={sigCanvasRef}
            onClose={() => setIsSigning(false)}
            onClear={() => sigCanvasRef.current?.clear()}
            onSave={handleSaveSignature}
          />
        )}
      </div>
        
  </PageLayoutWrapper>
);
}